// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { ChevronRight, Pencil, Plus, Trash2, Users, X } from 'lucide-react';

import { useDisqualifiedIndividuals } from '../../hooks/useDisqualifiedIndividuals';
import { isValidEmail } from '../../../../utils/email/isValidEmail';
import {
  PageHeader,
  Card,
  Input,
  Button,
  ConfirmDialog,
  workflowTableIconButtonEditClass,
  workflowTableIconButtonDangerClass,
} from '../../components/WorkflowFormPrimitives';
import { useCompanyContext } from '../../context/CompanyContext';

const COUNT_CHIP_CLASS =
  'inline-flex items-center justify-center gap-0.5 px-2.5 py-1 rounded-full text-xs font-medium tabular-nums shrink-0 bg-emerald-100 border border-emerald-200/70 text-emerald-900 shadow-sm';

function CategoryBlock({
  title,
  category,
  addLabel,
  addFirstLabel,
  emptyMessage,
  items,
  capMax,
  unlimited,
  color,
  description,
  canAddIndividuals,
  openModal,
  openEditModal,
  remove,
}) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const atCap = !unlimited && capMax != null && items.length >= capMax;
  const addDisabled = !canAddIndividuals || atCap;
  let addTitle;
  if (!canAddIndividuals) addTitle = 'Enter Headcount above before adding individuals';
  else if (atCap) addTitle = 'Maximum reached for this category';

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="flex items-center space-x-2 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${color}`}></div>
            <h3 className="text-xs font-medium text-gray-900">{title}</h3>
          </div>
          {unlimited ? (
            <span
              className={COUNT_CHIP_CLASS}
              aria-label={`${items.length} individual${items.length === 1 ? '' : 's'} in this category`}
            >
              {items.length}
            </span>
          ) : capMax != null ? (
            <span className={COUNT_CHIP_CLASS} aria-label={`${items.length} of ${capMax} maximum for this category`}>
              <span>{items.length}</span>
              <span className="mx-0.5 font-normal text-emerald-700/50">/</span>
              <span className="font-normal text-emerald-700/75">max {capMax}</span>
            </span>
          ) : (
            <span
              className={`${COUNT_CHIP_CLASS} text-emerald-800/70 font-normal`}
              aria-label="Enter Headcount above to compute category limits"
            >
              Set headcount
            </span>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={addDisabled}
          title={addTitle}
          onClick={() => openModal(category)}
        >
          <Plus size={16} className="mr-1 shrink-0" aria-hidden /> {addLabel}
        </Button>
      </div>

      {description && (
        <p className="text-xs text-gray-500 leading-relaxed mb-3 pb-3 border-b border-gray-100">{description}</p>
      )}

      {items.length > 0 ? (
        <div className="space-y-1">
          {items.map((ind) => (
            <div
              key={ind.id}
              className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-md min-w-0"
            >
              <div className="min-w-0 flex flex-col gap-0.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-xs font-medium text-gray-900 truncate">{ind.name}</span>
                  {ind.title ? (
                    <span className="text-xs text-gray-400 shrink-0 max-w-full truncate">{ind.title}</span>
                  ) : null}
                </div>
                {ind.email ? (
                  <span className="text-xs text-gray-500 truncate" title={ind.email}>
                    {ind.email}
                  </span>
                ) : null}
              </div>
              <div className="inline-flex shrink-0 items-center justify-center gap-1">
                <button
                  type="button"
                  onClick={() => openEditModal(ind)}
                  className={workflowTableIconButtonEditClass}
                  title="Edit individual"
                  aria-label={`Edit ${ind.name || 'individual'}`}
                >
                  <Pencil size={18} strokeWidth={2} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => setRemoveTarget({ id: ind.id, name: ind.name || 'this individual' })}
                  className={workflowTableIconButtonDangerClass}
                  title="Remove individual"
                  aria-label={`Remove ${ind.name || 'individual'}`}
                >
                  <Trash2 size={18} strokeWidth={2} aria-hidden />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-8 px-4 text-center" role="status">
          <Users size={24} className="mx-auto text-gray-300 mb-2" aria-hidden />
          <p className="text-xs text-gray-400">{emptyMessage}</p>
          <div className="flex justify-center mt-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={addDisabled}
              title={addTitle}
              onClick={() => openModal(category)}
            >
              <Plus size={16} className="mr-1 shrink-0" aria-hidden /> {addFirstLabel}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove Individual"
        description={
          removeTarget
            ? `Remove ${removeTarget.name} from ${title}? Related compensation and parachute entries for this person may need to be updated separately.`
            : undefined
        }
        confirmLabel="Remove"
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) remove(removeTarget.id);
          setRemoveTarget(null);
        }}
      />
    </Card>
  );
}

/* ─── DISQUALIFIED INDIVIDUALS ─── */
export const Individuals = ({ formData, setFormData, setActiveTab, continueMode }) => {
  const { isCurrentAnalysisLocked } = useCompanyContext();
  const {
    individuals,
    headcountNumber,
    hciLimit,
    officerLimit,
    hcis,
    officers,
    shareholders,
    showModal,
    editingId,
    newInd,
    setNewInd,
    openModal,
    openEditModal,
    closeModal,
    save,
    saveBusy,
    remove,
    onHeadcountChange,
    modalCategoryAtCap,
  } = useDisqualifiedIndividuals(formData, setFormData);

  const canAddIndividuals = headcountNumber > 0;
  const forwardPrimaryLabel = isCurrentAnalysisLocked ? 'Continue' : 'Save and continue';

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, closeModal]);

  return (
    <div className="space-y-5">
      <PageHeader title="Disqualified Individuals" subtitle="Identify Individuals Subject to 280G Analysis" />

      <Card>
        <p className="text-xs text-gray-600 leading-relaxed">
          In this step, you will identify the disqualified individuals of the company that are subject to the 280G
          analysis. An employee, contractor, or director who, at any time in the 12-month period prior to the closing
          date, fell into any of the three categories below (highly compensated individual, officer, 1% shareholder)
          will be a disqualified individual. Former employees, contractors, and directors could be picked up by the
          analysis if they fall into one of the categories. More information on each category is provided below.
        </p>

        <p className="text-xs text-gray-600 mt-3 leading-relaxed">
          Enter the company&apos;s highest number of employees on any day during the 12-month period prior to the
          closing date (used for regulatory HCI and officer limits).
        </p>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="max-w-xs">
            <Input
              label="Headcount"
              type="number"
              value={formData.headcount ?? ''}
              onChange={(e) => onHeadcountChange(e.target.value)}
              placeholder="Enter headcount"
              note="Highest employee count in the 12-month period before closing"
            />
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        <CategoryBlock
          title="Highly Compensated Individuals"
          category="hci"
          addLabel="Add HCI"
          addFirstLabel="Add First HCI"
          emptyMessage="No highly compensated individuals added yet"
          items={hcis}
          capMax={headcountNumber ? hciLimit : null}
          color="bg-emerald-600"
          description="The person must have annualized compensation during the Disqualified Individual Determination Period that is equal to or greater than the I.R.C. Section 414(q)(1)(B)(i) amount for the year in which the closing occurs (e.g., $160,000 in 2026)."
          canAddIndividuals={canAddIndividuals}
          openModal={openModal}
          openEditModal={openEditModal}
          remove={remove}
        />

        <CategoryBlock
          title="Officers"
          category="officer"
          addLabel="Add Officers"
          addFirstLabel="Add First Officer"
          emptyMessage="No officers added yet"
          items={officers}
          capMax={headcountNumber ? officerLimit : null}
          color="bg-violet-500"
          description="Officer status is determined based on all the facts and circumstances. An individual with an officer title is presumed to be an officer. An individual without an officer title can be a disqualified individual if the person has the authority of an officer (including policy-making authority)."
          canAddIndividuals={canAddIndividuals}
          openModal={openModal}
          openEditModal={openEditModal}
          remove={remove}
        />

        <CategoryBlock
          title="Shareholders (>1%)"
          category="shareholder"
          addLabel="Add Shareholders"
          addFirstLabel="Add First Shareholder"
          emptyMessage="No shareholders added yet"
          items={shareholders}
          unlimited
          color="bg-amber-500"
          description="An individual who provided services as an employee, consultant, or director during the Disqualified Individual Determination Period AND is a shareholder will be a disqualified individual if the shareholder owns over 1% of the fair market value of the outstanding shares of all classes of the corporation's stock at any time during the Disqualified Individual Determination Period. For purposes of the 1% calculation, (i) vested options held by an individual are considered to be owned by the individual (in addition to any actual shares the individual holds) and (ii) the constructive ownership rules in I.R.C. Section 318 apply."
          canAddIndividuals={canAddIndividuals}
          openModal={openModal}
          openEditModal={openEditModal}
          remove={remove}
        />
      </div>

      <div className="flex justify-between gap-3">
        <Button variant="secondary" onClick={() => setActiveTab('transaction')}>
          Previous
        </Button>
        {continueMode === 'dual' ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={individuals.length === 0}
              onClick={() => setActiveTab('compensation', { persist: false })}
            >
              Continue <ChevronRight size={14} className="ml-1.5" />
            </Button>
          </div>
        ) : (
          <Button disabled={individuals.length === 0} onClick={() => setActiveTab('compensation', { persist: true })}>
            {forwardPrimaryLabel} <ChevronRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="presentation">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal}></div>
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="individual-modal-title"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 id="individual-modal-title" className="text-xs font-medium text-gray-900">
                {editingId ? 'Edit Individual' : 'Add Individual'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2} aria-hidden />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <Input
                label="Full Name"
                value={newInd.name}
                onChange={(e) => setNewInd((p) => ({ ...p, name: e.target.value }))}
                placeholder="Enter full name"
              />
              <Input
                label="Email"
                type="email"
                value={newInd.email}
                onChange={(e) => setNewInd((p) => ({ ...p, email: e.target.value }))}
                placeholder="name@company.com"
                aria-invalid={newInd.email.trim().length > 0 && !isValidEmail(newInd.email)}
                note={
                  newInd.email.trim().length > 0 && !isValidEmail(newInd.email)
                    ? 'Enter a valid email address.'
                    : undefined
                }
              />
              <Input
                label="Title"
                value={newInd.title}
                onChange={(e) => setNewInd((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g., CEO, CFO"
              />
            </div>

            <div className="flex justify-end space-x-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <Button variant="secondary" size="sm" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={
                  !newInd.name.trim() ||
                  modalCategoryAtCap ||
                  saveBusy ||
                  (newInd.email.trim().length > 0 && !isValidEmail(newInd.email))
                }
                title={saveBusy ? 'Please wait' : modalCategoryAtCap ? 'Maximum reached for this category' : undefined}
              >
                {editingId ? 'Save changes' : 'Add Individual'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
